// ================================================================================
// CampaignModeIndicator.jsx — Dynamic campaign mode banner
// ================================================================================

import React, { useState } from "react";
import {
    Megaphone, Zap, Mail, Activity,
    Calendar, Timer, Users, Clock
} from "lucide-react";

const CampaignModeIndicator = ({ results, campaignInput }) => {
    const [selectedPulseDay, setSelectedPulseDay] = useState(null);

    // 1. Safe Access to Critical Data
    const timeBasedRec = results?.campaign_optimization?.budget_expansion_opportunities?.recommendations?.find(
        r => r.channel === 'strategic_canvassing' || r.channel === 'pulse_war_plan'
    );

    if (!timeBasedRec) return null;

    // 2. Data Extraction
    const windowDays = results?.input_summary?.execution_window_days || 0;
    const gapDays = timeBasedRec.gap_days || 0;
    const channelName = (timeBasedRec.channel || '').toLowerCase();

    const realAllocatedHours = results?.campaign_optimization?.optimal_budget_allocation?.total_hours_allocated || 0;

    // 3. Schedule Analysis
    const rawSchedule = timeBasedRec.weekly_schedule || {};
    const scheduleKeys = Object.keys(rawSchedule);
    const isWeekBased = scheduleKeys.some(k => k.toLowerCase().includes('week'));

    // 4. Mode Detection
    let currentMode = 'MARATHON';
    if (windowDays <= 1) currentMode = 'ACTION_DAY';
    else if (windowDays <= 3) currentMode = 'BLITZ';
    else if (gapDays > 14 && windowDays > 3) currentMode = 'FLIGHT';
    else if (windowDays <= 21) currentMode = 'PULSE';

    // 5. Configuration Dictionary
    const modeConfigs = {
        'ACTION_DAY': {
            theme: "rose", bgGradient: "from-rose-900/40 to-slate-900/80", borderColor: "border-rose-500/50",
            iconBg: "bg-rose-500/20", iconColor: "text-rose-400", iconBorder: "border-rose-500/30",
            MainIcon: Megaphone, title: "Action Day Active", tagBg: "bg-rose-500", tagText: "text-white",
            desc: "Single-day high-intensity mobilization. All resources concentrated into one operational window.",
            glow: "bg-rose-500/10", velocityColor: "text-rose-100"
        },
        'BLITZ': {
            theme: "rose", bgGradient: "from-rose-900/30 to-slate-900/70", borderColor: "border-rose-500/40",
            iconBg: "bg-rose-500/20", iconColor: "text-rose-400", iconBorder: "border-rose-500/30",
            MainIcon: Zap, title: "Weekend Blitz", tagBg: "bg-rose-500", tagText: "text-white",
            desc: "48-72 hour concentrated surge. Optimization prioritizes maximum contact density over this specific weekend.",
            glow: "bg-rose-500/10", velocityColor: "text-rose-100"
        },
        'FLIGHT': {
            theme: "indigo", bgGradient: "from-indigo-900/40 to-slate-900/80", borderColor: "border-indigo-500/40",
            iconBg: "bg-indigo-500/20", iconColor: "text-indigo-400", iconBorder: "border-indigo-500/20",
            MainIcon: Mail, title: "Flight Window Active", tagBg: "bg-indigo-500", tagText: "text-white",
            desc: `Campaign operates for ${windowDays} days before stopping ${gapDays} days out. Strategy prioritizes early saturation.`,
            glow: "bg-indigo-500/10", velocityColor: "text-indigo-100"
        },
        'PULSE': {
            theme: "amber", bgGradient: "from-amber-900/20 to-slate-900/60", borderColor: "border-amber-500/30",
            iconBg: "bg-amber-500/20", iconColor: "text-amber-400", iconBorder: "border-amber-500/20",
            MainIcon: Zap, title: "Tactical Pulse", tagBg: "bg-amber-500", tagText: "text-slate-900",
            desc: "Momentum calculations accelerated. Optimization is strictly bound to this high-intensity window.",
            glow: "bg-amber-500/5", velocityColor: "text-amber-100"
        },
        'MARATHON': {
            theme: "emerald", bgGradient: "from-emerald-900/20 to-slate-900/60", borderColor: "border-emerald-500/30",
            iconBg: "bg-emerald-500/20", iconColor: "text-emerald-400", iconBorder: "border-emerald-500/20",
            MainIcon: Activity, title: "Strategic Marathon", tagBg: "bg-emerald-500", tagText: "text-slate-900",
            desc: `Sustained pressure over ${Math.ceil(windowDays / 7)} weeks. Focuses on preventing volunteer burnout and maximizing retention.`,
            glow: "bg-emerald-500/5", velocityColor: "text-emerald-100"
        }
    };

    const config = modeConfigs[currentMode];

    // 6. Content Reactivity
    let displayRationale = timeBasedRec.rationale || timeBasedRec.timing_rationale || "Maximize frequency within the window.";
    if (channelName.includes('postal') || channelName.includes('leaflet') || channelName.includes('mail')) {
        displayRationale = "Strategy: Recurring drops scheduled to counter decay. High-frequency physical touchpoints required to anchor memory.";
    }

    // 7. Timeline Logic
    const showTimeline = !['ACTION_DAY', 'BLITZ'].includes(currentMode);

    const sortedKeys = showTimeline ? scheduleKeys.sort((a, b) => {
        if (isWeekBased) {
            const numA = parseInt(a.replace(/[^0-9]/g, '')) || 0;
            const numB = parseInt(b.replace(/[^0-9]/g, '')) || 0;
            return numB - numA;
        } else {
            const order = ['friday', 'saturday', 'sunday', 'monday', 'election'];
            const getIndex = (str) => order.findIndex(day => str.toLowerCase().includes(day));
            const idxA = getIndex(a);
            const idxB = getIndex(b);
            return (idxA !== -1 && idxB !== -1) ? idxA - idxB : a.localeCompare(b);
        }
    }) : [];

    // 8. Active Data Resolution
    let displayWindow, displayVols, displayHours, windowLabel;

    if (!showTimeline) {
        displayWindow = timeBasedRec.recommended_time_window;
        displayVols = timeBasedRec.min_volunteers_required;
        displayHours = realAllocatedHours > 0 ? realAllocatedHours : Object.values(rawSchedule).reduce((acc, curr) => {
            const h = typeof curr === 'object' ? (curr.hours || 0) : curr;
            return acc + h;
        }, 0);
        windowLabel = "Operational Window";
    } else {
        const activeData = (selectedPulseDay && rawSchedule[selectedPulseDay]) ? rawSchedule[selectedPulseDay] : null;
        displayWindow = activeData?.window || timeBasedRec.recommended_time_window;
        displayVols = activeData?.volunteers_needed || timeBasedRec.min_volunteers_required;
        displayHours = activeData?.hours || (realAllocatedHours > 0 ? realAllocatedHours : 0);
        windowLabel = selectedPulseDay ? (isWeekBased ? 'Weekly Shift' : 'Daily Shift') : 'Global Window';
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    };

    return (
        <div className={`p-5 rounded-xl border ${config.borderColor} bg-gradient-to-r ${config.bgGradient} relative overflow-hidden group transition-all duration-500`}>

            {/* Ambient Background Effects */}
            <div className={`absolute top-0 right-0 w-64 h-64 ${config.glow} rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none`}></div>
            <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-${config.theme}-500/50 to-transparent opacity-30`}></div>

            <div className="relative z-10 space-y-5">
                {/* TOP ROW: Header & Context */}
                <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                        <div className={`p-2.5 ${config.iconBg} rounded-lg ${config.iconColor} border ${config.iconBorder} shadow-lg backdrop-blur-sm`}>
                            <config.MainIcon size={22} />
                        </div>
                        <div>
                            <h4 className="text-lg font-bold text-white flex items-center gap-3">
                                {config.title}
                                <span className={`text-[10px] uppercase ${config.tagBg} ${config.tagText} px-2 py-0.5 rounded-md font-black tracking-wider shadow-sm`}>
                                    {windowDays} Day Window
                                </span>
                            </h4>
                            <p className="text-xs text-slate-300 mt-1.5 leading-relaxed max-w-lg">{config.desc}</p>

                            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4">
                                <div className={`flex items-center gap-2 text-xs ${config.theme === 'rose' ? 'text-rose-200/80' : config.theme === 'amber' ? 'text-amber-200/80' : 'text-emerald-200/80'}`}>
                                    <Calendar size={14} className={config.iconColor} />
                                    <span className={`font-mono font-medium ${config.velocityColor}`}>
                                        {formatDate(campaignInput?.execution_start_date)} → {formatDate(campaignInput?.execution_end_date)}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                                    <Timer size={14} />
                                    <span>Intensity: <span className="text-white font-bold">{currentMode === 'ACTION_DAY' ? 'Max' : currentMode === 'BLITZ' ? 'Very High' : 'Standard'}</span></span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Visual Graph Animation */}
                    <div className="hidden sm:flex items-end gap-1 h-12 opacity-60">
                        <div className={`w-1.5 bg-${config.theme}-500/20 h-4 rounded-full`}></div>
                        <div className={`w-1.5 bg-${config.theme}-500/30 h-6 rounded-full`}></div>
                        <div className={`w-1.5 bg-${config.theme}-500/50 h-3 rounded-full`}></div>
                        <div className={`w-1.5 bg-${config.theme}-500/80 h-8 rounded-full animate-pulse`}></div>
                        <div className={`w-1.5 bg-${config.theme}-500 h-10 rounded-full shadow-[0_0_10px_rgba(var(--${config.theme}-500),0.5)]`}></div>
                        <div className={`w-1.5 bg-${config.theme}-500/80 h-5 rounded-full`}></div>
                        <div className={`w-1.5 bg-${config.theme}-500/40 h-3 rounded-full`}></div>
                    </div>
                </div>

                {/* MIDDLE ROW: Operational Strategy */}
                <div className={`border-t ${config.borderColor} pt-4 mt-2 animate-in fade-in slide-in-from-bottom-2 duration-500`}>
                    <div className="mb-5">
                        <h6 className={`text-[10px] font-bold ${config.iconColor} uppercase tracking-wider flex items-center gap-1.5 mb-2`}>
                            <Activity size={12} /> Operational Strategy
                        </h6>
                        <p className={`text-sm text-slate-200 italic leading-relaxed pl-1 border-l-2 ${config.borderColor}`}>
                            "{displayRationale}"
                        </p>
                    </div>

                    {/* Interactive Timeline */}
                    {showTimeline && sortedKeys.length > 0 && (
                        <div className="mb-5">
                            <div className="flex space-x-2 overflow-x-auto pb-2 custom-scrollbar mask-linear-fade">
                                {sortedKeys.map((key) => (
                                    <button
                                        key={key}
                                        onClick={() => setSelectedPulseDay(selectedPulseDay === key ? null : key)}
                                        className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 border ${
                                            selectedPulseDay === key
                                            ? `bg-${config.theme}-500 text-white border-${config.theme}-400 font-bold shadow-md`
                                            : `bg-${config.theme}-900/20 text-slate-400 border-${config.theme}-500/10 hover:bg-${config.theme}-900/40 hover:text-white hover:border-${config.theme}-500/30`
                                        }`}
                                    >
                                        {key.includes('Week')
                                            ? key.replace('Week', 'W')
                                            : key.split('(')[0].replace('Action Day', '').trim() || key.split('(')[0].trim()
                                        }
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Metrics Dashboard */}
                    <div className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-1 min-h-[40px]">
                        {displayWindow && (
                            <div className="flex items-center gap-2 animate-in fade-in duration-300" key={`win-${selectedPulseDay || 'global'}`}>
                                <Clock size={16} className={config.iconColor} />
                                <div className="flex flex-col">
                                    <span className={`text-[9px] text-${config.theme}-500/60 uppercase font-bold tracking-wider`}>{windowLabel}</span>
                                    <span className="text-sm font-bold text-white leading-none">{displayWindow}</span>
                                </div>
                            </div>
                        )}

                        {(displayHours !== undefined && displayHours > 0) ? (
                            <div className="flex items-center gap-2 animate-in fade-in duration-300" key={`hrs-${selectedPulseDay || 'global'}`}>
                                <Timer size={16} className="text-orange-400" />
                                <div className="flex flex-col">
                                    <span className="text-[9px] text-orange-500/60 uppercase font-bold tracking-wider">Target Hours</span>
                                    <span className="text-sm font-bold text-white leading-none font-mono">{displayHours.toFixed(1)} Hrs</span>
                                </div>
                            </div>
                        ) : (
                            timeBasedRec.gap_days !== undefined && !['ACTION_DAY', 'BLITZ'].includes(currentMode) && (
                                <div className="flex items-center gap-2 animate-in fade-in duration-300" key="gap-global">
                                    <Calendar size={16} className="text-slate-400" />
                                    <div className="flex flex-col">
                                        <span className="text-[9px] text-slate-500 uppercase font-bold tracking-wider">Campaign Gap</span>
                                        <span className="text-sm font-bold text-white leading-none font-mono">{timeBasedRec.gap_days} Days</span>
                                    </div>
                                </div>
                            )
                        )}

                        {displayVols !== undefined && (
                            <div className="flex items-center gap-2 animate-in fade-in duration-300" key={`vol-${selectedPulseDay || 'global'}`}>
                                <Users size={16} className={`text-${config.theme}-400`} />
                                <div className="flex flex-col">
                                    <span className={`text-[9px] text-${config.theme}-500/60 uppercase font-bold tracking-wider`}>
                                        {selectedPulseDay ? 'Vols Needed' : 'Peak Vols'}
                                    </span>
                                    <span className="text-sm font-bold text-white leading-none font-mono">{displayVols} Active</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CampaignModeIndicator;
